import { useFormikContext } from 'formik'
import { ChangeEvent, DragEvent, useRef, useState } from 'react'
import { MailModel } from '../../../../../../shared/models/Mail.model'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCloudUpload } from '@fortawesome/free-solid-svg-icons'
import { UserUploadedFileModel } from '../../../../../../shared/models/service-models/UserUploadedFile.model'
import {
  ACCEPTED_EXTENSIONS,
  ACCEPT_ATTRIBUTE,
  getFileExtension,
  useEmailAttachmentsAppender,
} from '../useEmailAttachmentsAppender'

const formatFileSize = (size: number) => {
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`
  return `${(size / (1024 * 1024)).toFixed(2)} MB`
}

const getIconNameByExtension = (extension: string) => {
  if (extension === '.doc' || extension === '.docx' || extension === '.txt') return 'doc'
  if (extension === '.xls' || extension === '.xlsx' || extension === '.csv') return 'xlsx'
  if (extension === '.ppt' || extension === '.pptx') return 'ppt'
  if (extension === '.pdf') return 'pdf'
  if (
    extension === '.jpg' ||
    extension === '.jpeg' ||
    extension === '.png' ||
    extension === '.gif' ||
    extension === '.bmp'
  ) {
    return 'img'
  }
  if (extension === '.zip') return 'zip'
  if (extension === '.msg') return 'msg'
  return 'doc'
}

const getFileIconSrc = (fileName: string) => {
  const extension = getFileExtension(fileName)
  const iconName = getIconNameByExtension(extension)
  return `/images/file-type-icons/${iconName}.svg`
}

type Props = {
  isValidSize: (
    newAttachments: Pick<UserUploadedFileModel, 'originalFileName' | 'fileSize'>[]
  ) => boolean
  maxTotalSizeMB: number
  disabled: boolean
}

const AttachmentsField = ({ isValidSize, maxTotalSizeMB, disabled }: Props) => {
  const [isDragActive, setIsDragActive] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const { values, setFieldValue } = useFormikContext<MailModel>()
  const { appendFiles } = useEmailAttachmentsAppender({ isValidSize, maxTotalSizeMB })

  const files = values.attachments ?? []
  const dropZoneClassName = `min-h-[54px] cursor-pointer rounded border border-dashed p-2 transition-colors duration-150 border-blue-400 ${
    isDragActive ? 'bg-blue-50' : 'bg-white'
  }`

  const supportedExtensions = new Intl.ListFormat('en', {
    style: 'long',
    type: 'conjunction',
  }).format(ACCEPTED_EXTENSIONS.map(ext => ext.replace('.', '').toUpperCase()))

  const supportedFormatsText = `Supported file formats: ${supportedExtensions}. Maximum total upload size: ${maxTotalSizeMB} MB.`

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files ?? [])
    appendFiles(selectedFiles)
    event.target.value = ''
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    if (disabled) return

    event.preventDefault()
    event.stopPropagation()
    setIsDragActive(false)
    const droppedFiles = Array.from(event.dataTransfer.files ?? [])
    appendFiles(droppedFiles)
  }

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragActive(true)
  }

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    if (!isDragActive) setIsDragActive(true)
  }

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()

    const nextTarget = event.relatedTarget as Node | null
    if (nextTarget && event.currentTarget.contains(nextTarget)) {
      return
    }

    setIsDragActive(false)
  }

  const handleRemoveFile = (fileIdToRemove: string) => {
    const nextFiles = files.filter(file => file.id !== fileIdToRemove)
    setFieldValue('attachments', nextFiles)
  }

  return (
    <div className="border border-gray-300">
      <div className="p-2">
        <div
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              inputRef.current?.click()
            }
          }}
          onDrop={handleDrop}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={dropZoneClassName}
        >
          <div className="text-center text-xs font-medium text-gray-800 italic">
            <FontAwesomeIcon icon={faCloudUpload} className="mr-1" /> Click or Drag and Drop files
            here to upload.
          </div>
          <div className="mt-1 text-center text-[10px] leading-[1.4] text-gray-500 italic">
            {supportedFormatsText}
          </div>
        </div>

        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT_ATTRIBUTE}
          className="hidden"
          onChange={handleInputChange}
          disabled={disabled}
        />

        {files.length ? (
          <div className="mt-2 flex flex-wrap gap-2">
            {files.map(file => (
              <div
                key={file.id}
                className="inline-flex max-w-[280px] items-center gap-2 rounded border border-gray-300  px-2 py-1.5"
              >
                <img
                  src={getFileIconSrc(file.originalFileName)}
                  alt={`${getFileExtension(file.originalFileName).replace('.', '').toUpperCase() || 'File'} icon`}
                  className="h-[2rem] w-[2rem] flex-shrink-0"
                />
                <div className="min-w-0">
                  <div
                    title={file.originalFileName}
                    className="truncate whitespace-nowrap text-xs text-gray-900"
                  >
                    {file.originalFileName}
                  </div>
                  <div className="text-[11px] text-gray-500">{formatFileSize(file.fileSize)}</div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveFile(file.id)}
                  aria-label={`Remove ${file.originalFileName}`}
                  className="p-0 text-base leading-none text-gray-500 transition-colors hover:text-gray-700"
                  style={{ background: 'none' }}
                >
                  x
                </button>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default AttachmentsField
